// Layout-edit page for the Manage Layouts flow.
//
// Replaces the Manage Layouts table when the user clicks the edit icon
// on a row. Mirrors the reference's LayoutEditor + LineItemsTab
// (ocr-ui/.../config-studio/rules-editor/{LayoutEditor,LineItemsTab}.jsx)
// — KEY/VALUE top section + collapsible Column Segmentation, Line Item
// Splitting, and Advanced Option (Header / Footer Identification) groups.
//
// Data sources:
//   POST /document_extraction/management_layout/layout_id_select
//   POST /document_extraction/management_layout/rule_delimiter_list
//   POST /document_extraction/management_layout/workflow_list
//   POST /document_extraction/management_layout/layout_id_update

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Settings,
  Trash2,
  X,
} from "lucide-react";

import { ocrApi } from "../../../services/ocrApi";

interface ManageLayoutEditViewProps {
  layoutId: string;
  /** Fires when the user clicks Back to return to the layouts list. */
  onBack: () => void;
}

interface MethodOptions {
  columnSegmentation: string[];
  lineItemSplitting: string[];
  headerIdentification: string[];
  footerIdentification: string[];
}

const EMPTY_METHOD_OPTIONS: MethodOptions = {
  columnSegmentation: [],
  lineItemSplitting: [],
  headerIdentification: [],
  footerIdentification: [],
};

interface ColumnBoundary {
  xmin: string;
  xmax: string;
}

/** Coerce the workflow_list response into a string[]. Shape varies. */
function pickWorkflowIds(raw: unknown): string[] {
  const fromArray = (arr: unknown[]): string[] =>
    arr
      .map((item) =>
        typeof item === "string"
          ? item
          : typeof (item as { workflow_id?: unknown })?.workflow_id === "string"
            ? String((item as { workflow_id: string }).workflow_id)
            : null,
      )
      .filter((s): s is string => !!s);
  if (Array.isArray(raw)) return fromArray(raw);
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    const candidates =
      (Array.isArray(obj.data) ? obj.data : null) ??
      (Array.isArray(obj.workflows) ? obj.workflows : null) ??
      (Array.isArray(obj.status) ? obj.status : null);
    if (candidates) return fromArray(candidates);
    const mapCandidate =
      (obj.data && typeof obj.data === "object" && !Array.isArray(obj.data)
        ? (obj.data as Record<string, unknown>)
        : null) ??
      (obj.workflows &&
      typeof obj.workflows === "object" &&
      !Array.isArray(obj.workflows)
        ? (obj.workflows as Record<string, unknown>)
        : null);
    if (mapCandidate) return Object.keys(mapCandidate);
  }
  return [];
}

/** Coerce the rule_delimiter_list response into method-name arrays. */
function pickMethodOptions(raw: unknown): MethodOptions {
  const pickArray = (obj: Record<string, unknown>, key: string): string[] => {
    const v = obj[key];
    if (Array.isArray(v)) {
      return v
        .map((item) =>
          typeof item === "string"
            ? item
            : typeof (item as { value?: unknown })?.value === "string"
              ? String((item as { value: string }).value)
              : null,
        )
        .filter((s): s is string => !!s);
    }
    return [];
  };

  let source: Record<string, unknown> = {};
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (obj.data && typeof obj.data === "object" && !Array.isArray(obj.data)) {
      source = obj.data as Record<string, unknown>;
    } else {
      source = obj;
    }
  }

  return {
    columnSegmentation: pickArray(source, "column_segmentation"),
    lineItemSplitting: pickArray(source, "line_item_splitting"),
    headerIdentification: pickArray(source, "header_identification"),
    footerIdentification: pickArray(source, "footer_identification"),
  };
}

function formatMethodLabel(value: string): string {
  if (!value) return "";
  return value
    .split(/[\s_]+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ""))
    .join(" ");
}

export function ManageLayoutEditView({
  layoutId,
  onBack,
}: ManageLayoutEditViewProps) {
  const queryClient = useQueryClient();

  // ------- Form state ---------------------------------------------------
  const [layoutName, setLayoutName] = useState("");
  const [workflowId, setWorkflowId] = useState("");
  const [workflowEditable, setWorkflowEditable] = useState(false);

  // Method choices
  const [columnSegmentation, setColumnSegmentation] = useState("");
  const [lineItemSplitting, setLineItemSplitting] = useState("");
  const [headerIdentification, setHeaderIdentification] = useState("");
  const [footerIdentification, setFooterIdentification] = useState("");

  // Column data (driven by Column Segmentation method)
  const [headerFieldNames, setHeaderFieldNames] = useState<string[]>([]);
  const [columnBoundary, setColumnBoundary] = useState<
    Record<string, ColumnBoundary>
  >({});
  const [selectedColumn, setSelectedColumn] = useState<string>("");

  // Header identification data (driven by method)
  const [headerKeywords, setHeaderKeywords] = useState<string[][]>([]);
  const [headerBoundary, setHeaderBoundary] = useState<string[]>([
    "",
    "",
    "",
    "",
  ]);

  // Footer identification data
  const [footerKeywords, setFooterKeywords] = useState<string[]>([]);
  const [footerBoundary, setFooterBoundary] = useState<string>("");

  // Section open/closed state
  const [openColumns, setOpenColumns] = useState(true);
  const [openSplitting, setOpenSplitting] = useState(true);
  const [openAdvanced, setOpenAdvanced] = useState(false);
  const [openHeader, setOpenHeader] = useState(true);
  const [openFooter, setOpenFooter] = useState(true);

  // ----- Queries --------------------------------------------------------
  const layoutQuery = useQuery({
    queryKey: ["ocr", "layout-detail", layoutId],
    queryFn: () => ocrApi.getLayout(layoutId),
    enabled: !!layoutId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const workflowsQuery = useQuery({
    queryKey: ["ocr", "workflows"],
    queryFn: () => ocrApi.listWorkflows(),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const ruleOptionsQuery = useQuery({
    queryKey: ["ocr", "rule-delimiter-options"],
    queryFn: () => ocrApi.fetchRuleDelimiterOptions(),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const workflows = useMemo(
    () => pickWorkflowIds(workflowsQuery.data),
    [workflowsQuery.data],
  );

  const methodOptions = useMemo<MethodOptions>(
    () =>
      ruleOptionsQuery.data
        ? pickMethodOptions(ruleOptionsQuery.data)
        : EMPTY_METHOD_OPTIONS,
    [ruleOptionsQuery.data],
  );

  // Hydrate the form once layout details land.
  useEffect(() => {
    const data = layoutQuery.data as Record<string, unknown> | undefined;
    if (!data) return;

    setLayoutName(
      typeof data.layout_name === "string"
        ? data.layout_name
        : typeof data.customer_name === "string"
          ? data.customer_name
          : "",
    );
    setWorkflowId(typeof data.workflow_id === "string" ? data.workflow_id : "");

    // The reference's mapLayoutData unpacks methods + keywords from a few
    // possible shapes. We accept either the nested `rules.lineItems` form
    // or the backend's `delimiter_config` + `line_item_rules[0]` form.
    const rules = (data.rules ?? {}) as Record<string, unknown>;
    const lineItems =
      (rules.lineItems as Record<string, unknown> | undefined) ?? {};
    const lineItemRules = Array.isArray(data.line_item_rules)
      ? (data.line_item_rules[0] as Record<string, unknown> | undefined)
      : undefined;
    const delimiterConfig = (data.delimiter_config ??
      rules.delimiter_config ??
      {}) as Record<string, unknown>;

    const pickRule = (snake: string, camel: string): string => {
      if (typeof lineItems[camel] === "string") return lineItems[camel] as string;
      if (typeof lineItems[snake] === "string") return lineItems[snake] as string;
      if (lineItemRules && typeof lineItemRules[snake] === "string") {
        return lineItemRules[snake] as string;
      }
      return "";
    };

    setColumnSegmentation(pickRule("column_segmentation", "columnSegmentation"));
    setLineItemSplitting(pickRule("line_item_splitting", "lineItemSplitting"));
    setHeaderIdentification(
      pickRule("header_identification", "headerIdentification"),
    );
    setFooterIdentification(
      pickRule("footer_identification", "footerIdentification"),
    );

    // Column names + boundaries.
    const headerCfg =
      (delimiterConfig.header as Record<string, unknown> | undefined) ?? {};
    const columnCfg =
      (delimiterConfig.column as Record<string, unknown> | undefined) ?? {};
    const fieldNames = Array.isArray(headerCfg.field_names)
      ? (headerCfg.field_names as unknown[]).filter(
          (n): n is string => typeof n === "string" && n.length > 0,
        )
      : Array.isArray(lineItems.headerFieldNames)
        ? (lineItems.headerFieldNames as unknown[]).filter(
            (n): n is string => typeof n === "string" && n.length > 0,
          )
        : [];
    setHeaderFieldNames(fieldNames);

    const rawBoundary =
      (columnCfg.column_boundary as Record<string, unknown> | undefined) ??
      (lineItems.columnBoundary as Record<string, unknown> | undefined) ??
      {};
    const coercedBoundary: Record<string, ColumnBoundary> = {};
    for (const name of fieldNames) {
      const b = rawBoundary[name] as Record<string, unknown> | undefined;
      coercedBoundary[name] = {
        xmin: b && b.xmin != null ? String(b.xmin) : "",
        xmax: b && b.xmax != null ? String(b.xmax) : "",
      };
    }
    setColumnBoundary(coercedBoundary);

    // Index column selection.
    const idxCol = (delimiterConfig.lineitem as Record<string, unknown> | undefined)
      ?.index_column;
    if (typeof idxCol === "string") {
      setSelectedColumn(idxCol);
    } else if (Array.isArray(idxCol) && idxCol.length > 0) {
      setSelectedColumn(`column_${Number(idxCol[0]) + 1}`);
    } else if (typeof lineItems.selectedColumn === "string") {
      setSelectedColumn(lineItems.selectedColumn as string);
    }

    // Header keywords / boundary.
    const hkRaw =
      (headerCfg.header_keywords as unknown[] | undefined) ??
      (lineItems.headerKeywords as unknown[] | undefined) ??
      [];
    const hkClean: string[][] = hkRaw.map((col) =>
      Array.isArray(col)
        ? (col as unknown[]).filter(
            (k): k is string => typeof k === "string",
          )
        : [],
    );
    setHeaderKeywords(hkClean);

    const hbRaw =
      (headerCfg.header_boundary as unknown[] | undefined) ??
      (lineItems.headerBoundary as unknown[] | undefined) ??
      [];
    setHeaderBoundary([
      String(hbRaw[0] ?? ""),
      String(hbRaw[1] ?? ""),
      String(hbRaw[2] ?? ""),
      String(hbRaw[3] ?? ""),
    ]);

    // Footer keywords / boundary.
    const footerCfg =
      (delimiterConfig.footer as Record<string, unknown> | undefined) ?? {};
    const fkRaw =
      (footerCfg.footer_keyword as unknown[] | undefined) ??
      (lineItems.footerKeywords as unknown[] | undefined) ??
      [];
    setFooterKeywords(
      fkRaw.filter((k): k is string => typeof k === "string"),
    );
    setFooterBoundary(
      footerCfg.footer_boundary != null
        ? String(footerCfg.footer_boundary)
        : lineItems.footerBoundary != null
          ? String(lineItems.footerBoundary)
          : "",
    );
  }, [layoutQuery.data]);

  // ----- Column boundary helpers ---------------------------------------
  const addColumn = () => {
    const name = `column_${headerFieldNames.length + 1}`;
    setHeaderFieldNames((prev) => [...prev, name]);
    setColumnBoundary((prev) => ({ ...prev, [name]: { xmin: "", xmax: "" } }));
    setHeaderKeywords((prev) => [...prev, []]);
  };

  const renameColumn = (idx: number, nextName: string) => {
    const oldName = headerFieldNames[idx];
    if (!nextName || nextName === oldName) {
      const names = [...headerFieldNames];
      names[idx] = nextName;
      setHeaderFieldNames(names);
      return;
    }
    setHeaderFieldNames((prev) => {
      const next = [...prev];
      next[idx] = nextName;
      return next;
    });
    setColumnBoundary((prev) => {
      const next = { ...prev };
      next[nextName] = next[oldName] ?? { xmin: "", xmax: "" };
      delete next[oldName];
      return next;
    });
  };

  const removeColumn = (idx: number) => {
    const name = headerFieldNames[idx];
    setHeaderFieldNames((prev) => prev.filter((_, i) => i !== idx));
    setColumnBoundary((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setHeaderKeywords((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateBoundary = (
    name: string,
    key: keyof ColumnBoundary,
    value: string,
  ) => {
    setColumnBoundary((prev) => ({
      ...prev,
      [name]: { ...(prev[name] ?? { xmin: "", xmax: "" }), [key]: value },
    }));
  };

  // ----- Header / Footer keyword helpers -------------------------------
  const addHeaderKeyword = (colIdx: number) => {
    setHeaderKeywords((prev) => {
      const next = prev.map((col) => [...col]);
      while (next.length <= colIdx) next.push([]);
      next[colIdx].push("");
      return next;
    });
  };
  const setHeaderKeyword = (colIdx: number, kwIdx: number, value: string) => {
    setHeaderKeywords((prev) => {
      const next = prev.map((col) => [...col]);
      if (next[colIdx]) next[colIdx][kwIdx] = value;
      return next;
    });
  };
  const removeHeaderKeyword = (colIdx: number, kwIdx: number) => {
    setHeaderKeywords((prev) => {
      const next = prev.map((col) => [...col]);
      if (next[colIdx]) next[colIdx].splice(kwIdx, 1);
      return next;
    });
  };

  const addFooterKeyword = () => setFooterKeywords((prev) => [...prev, ""]);
  const setFooterKeyword = (i: number, v: string) =>
    setFooterKeywords((prev) => prev.map((kw, idx) => (idx === i ? v : kw)));
  const removeFooterKeyword = (i: number) =>
    setFooterKeywords((prev) => prev.filter((_, idx) => idx !== i));

  // ----- Mutation ------------------------------------------------------
  const saveMutation = useMutation({
    mutationFn: () =>
      ocrApi.upsertLayout({
        layoutId,
        customerName: layoutName,
        workflowId,
      }),
    onSuccess: () => {
      toast.success(`Layout "${layoutId}" saved`);
      queryClient.invalidateQueries({ queryKey: ["ocr", "layouts"] });
      queryClient.invalidateQueries({
        queryKey: ["ocr", "layout-detail", layoutId],
      });
    },
    onError: (err: unknown) => {
      toast.error("Save failed", {
        description: err instanceof Error ? err.message : String(err),
      });
    },
  });

  const layoutError =
    layoutQuery.error instanceof Error ? layoutQuery.error.message : null;
  const showColumnList =
    columnSegmentation === "rule" || columnSegmentation === "index";
  const showIndexColumn = lineItemSplitting === "index";

  return (
    <div className="flex h-full w-full flex-col overflow-auto bg-gray-50">
      {/* === Page header ============================================== */}
      <header className="flex items-start justify-between gap-4 border-b border-gray-200 bg-white px-8 py-5">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-3 bg-blue-50 rounded-xl flex-shrink-0">
            <Settings size={22} className="text-blue-600" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-gray-900">
              Manage Layout
            </h1>
            <p className="text-sm text-gray-500">
              Configure document extraction rules and layouts.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 flex-shrink-0"
        >
          <ArrowLeft size={16} className="text-gray-500" />
          Back to Manage Layouts
        </button>
      </header>

      {/* === Card body ================================================ */}
      <div className="flex-1 px-8 py-6">
        {layoutQuery.isLoading ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-16 text-center text-sm text-gray-500 shadow-sm">
            Loading layout…
          </div>
        ) : layoutQuery.isError ? (
          <div className="mx-auto max-w-md rounded-md border border-rose-200 bg-rose-50 p-4 text-rose-700">
            <p className="font-medium">Couldn't load layout.</p>
            <p className="mt-1 text-rose-600">{layoutError ?? "Unknown error"}</p>
            <button
              type="button"
              onClick={() => layoutQuery.refetch()}
              className="mt-3 inline-flex items-center rounded-md border border-rose-300 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-100"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* KEY / VALUE top card */}
            <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="grid grid-cols-[180px_1fr] bg-gray-50 px-6 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                <div>Key</div>
                <div>Value</div>
              </div>
              <div className="divide-y divide-gray-100">
                <KeyValueRow label="layout_id">
                  <input
                    type="text"
                    value={layoutId}
                    readOnly
                    className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
                  />
                </KeyValueRow>
                <KeyValueRow label="layout_name">
                  <input
                    type="text"
                    value={layoutName}
                    onChange={(e) => setLayoutName(e.target.value)}
                    placeholder="Optional"
                    className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </KeyValueRow>
                <KeyValueRow label="workflow_id">
                  <div className="flex items-center gap-2">
                    <select
                      value={workflowId}
                      onChange={(e) => setWorkflowId(e.target.value)}
                      disabled={!workflowEditable && !!workflowId}
                      className="flex-1 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
                    >
                      <option value="" disabled>
                        {workflowsQuery.isLoading
                          ? "Loading workflows…"
                          : "Select a workflow"}
                      </option>
                      {workflows.map((w) => (
                        <option key={w} value={w}>
                          {w}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setWorkflowEditable((v) => !v)}
                      className={`rounded-md border p-2 text-sm transition ${
                        workflowEditable
                          ? "border-blue-500 bg-blue-50 text-blue-600"
                          : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
                      }`}
                      title={
                        workflowEditable ? "Lock workflow" : "Edit workflow"
                      }
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </div>
                </KeyValueRow>
              </div>
            </section>

            {/* === Column Segmentation =================================== */}
            <CollapsibleSection
              title="Column Segmentation"
              open={openColumns}
              onToggle={() => setOpenColumns((v) => !v)}
              right={
                showColumnList ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      addColumn();
                    }}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Column
                  </button>
                ) : null
              }
            >
              <KeyValueRow label="Method">
                <MethodSelect
                  value={columnSegmentation}
                  onChange={setColumnSegmentation}
                  options={methodOptions.columnSegmentation}
                  loading={ruleOptionsQuery.isLoading}
                />
              </KeyValueRow>
              {showColumnList ? (
                headerFieldNames.length === 0 ? (
                  <div className="px-6 py-3 text-xs italic text-gray-500">
                    No columns yet. Click <strong>Add Column</strong> above to
                    create one.
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {headerFieldNames.map((name, idx) => (
                      <div
                        key={`${name}-${idx}`}
                        className="grid grid-cols-[180px_1fr] items-center gap-4 px-6 py-3"
                      >
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => renameColumn(idx, e.target.value)}
                          placeholder="Column name"
                          className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <div className="flex items-center gap-3">
                          <CoordInput
                            label="xmin"
                            value={columnBoundary[name]?.xmin ?? ""}
                            onChange={(v) => updateBoundary(name, "xmin", v)}
                          />
                          <CoordInput
                            label="xmax"
                            value={columnBoundary[name]?.xmax ?? ""}
                            onChange={(v) => updateBoundary(name, "xmax", v)}
                          />
                          <button
                            type="button"
                            onClick={() => removeColumn(idx)}
                            className="ml-auto rounded-md p-1.5 text-rose-500 hover:bg-rose-50"
                            title="Remove column"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : null}
            </CollapsibleSection>

            {/* === Line Item Splitting =================================== */}
            <CollapsibleSection
              title="Line Item Splitting"
              open={openSplitting}
              onToggle={() => setOpenSplitting((v) => !v)}
            >
              <KeyValueRow label="Method">
                <MethodSelect
                  value={lineItemSplitting}
                  onChange={setLineItemSplitting}
                  options={methodOptions.lineItemSplitting}
                  loading={ruleOptionsQuery.isLoading}
                />
              </KeyValueRow>
              {showIndexColumn ? (
                <KeyValueRow label="Index Column">
                  <select
                    value={selectedColumn}
                    onChange={(e) => setSelectedColumn(e.target.value)}
                    className={`w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                      selectedColumn ? "text-gray-900" : "text-gray-400"
                    }`}
                  >
                    <option value="" disabled>
                      {headerFieldNames.length === 0
                        ? "Add columns first"
                        : "Select an index column"}
                    </option>
                    {headerFieldNames.map((name, i) => (
                      <option key={name} value={`column_${i + 1}`}>
                        Column {i + 1} ({name})
                      </option>
                    ))}
                  </select>
                </KeyValueRow>
              ) : null}
            </CollapsibleSection>

            {/* === Advanced Option ===================================== */}
            <CollapsibleSection
              title="Advanced Option"
              open={openAdvanced}
              onToggle={() => setOpenAdvanced((v) => !v)}
            >
              <div className="space-y-3 p-3">
                {/* Header Identification */}
                <CollapsibleSection
                  title="Header Identification"
                  open={openHeader}
                  onToggle={() => setOpenHeader((v) => !v)}
                  variant="nested"
                >
                  <KeyValueRow label="Method">
                    <MethodSelect
                      value={headerIdentification}
                      onChange={setHeaderIdentification}
                      options={methodOptions.headerIdentification}
                      loading={ruleOptionsQuery.isLoading}
                    />
                  </KeyValueRow>
                  {headerIdentification === "keyword" ? (
                    headerFieldNames.length === 0 ? (
                      <div className="px-6 py-3 text-xs italic text-gray-500">
                        Add columns under Column Segmentation first to attach
                        header keywords.
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {headerFieldNames.map((name, colIdx) => (
                          <div
                            key={`${name}-${colIdx}`}
                            className="grid grid-cols-[180px_1fr] items-start gap-4 px-6 py-3"
                          >
                            <div className="pt-1 text-sm font-medium text-gray-700">
                              Column: {name}
                            </div>
                            <KeywordList
                              keywords={headerKeywords[colIdx] ?? []}
                              onAdd={() => addHeaderKeyword(colIdx)}
                              onChange={(kwIdx, v) =>
                                setHeaderKeyword(colIdx, kwIdx, v)
                              }
                              onRemove={(kwIdx) =>
                                removeHeaderKeyword(colIdx, kwIdx)
                              }
                            />
                          </div>
                        ))}
                      </div>
                    )
                  ) : headerIdentification === "boundary" ? (
                    <div className="grid grid-cols-4 gap-3 px-6 py-3">
                      {(["xmin", "ymin", "xmax", "ymax"] as const).map(
                        (label, i) => (
                          <CoordInput
                            key={label}
                            label={label}
                            value={headerBoundary[i] ?? ""}
                            onChange={(v) =>
                              setHeaderBoundary((prev) => {
                                const next = [...prev];
                                next[i] = v;
                                return next;
                              })
                            }
                          />
                        ),
                      )}
                    </div>
                  ) : null}
                </CollapsibleSection>

                {/* Footer Identification */}
                <CollapsibleSection
                  title="Footer Identification"
                  open={openFooter}
                  onToggle={() => setOpenFooter((v) => !v)}
                  variant="nested"
                >
                  <KeyValueRow label="Method">
                    <MethodSelect
                      value={footerIdentification}
                      onChange={setFooterIdentification}
                      options={methodOptions.footerIdentification}
                      loading={ruleOptionsQuery.isLoading}
                    />
                  </KeyValueRow>
                  {footerIdentification === "keyword" ? (
                    <div className="grid grid-cols-[180px_1fr] items-start gap-4 px-6 py-3">
                      <div className="pt-1 text-sm font-medium text-gray-700">
                        Keywords
                      </div>
                      <KeywordList
                        keywords={footerKeywords}
                        onAdd={addFooterKeyword}
                        onChange={setFooterKeyword}
                        onRemove={removeFooterKeyword}
                      />
                    </div>
                  ) : footerIdentification === "boundary" ? (
                    <div className="grid grid-cols-[180px_1fr] items-center gap-4 px-6 py-3">
                      <div className="text-sm font-medium text-gray-700">
                        Y Boundary
                      </div>
                      <CoordInput
                        label="y"
                        value={footerBoundary}
                        onChange={setFooterBoundary}
                      />
                    </div>
                  ) : null}
                </CollapsibleSection>
              </div>
            </CollapsibleSection>
          </div>
        )}
      </div>

      {/* === Bottom action bar — Cancel + Retrigger =================== */}
      <footer className="border-t border-gray-200 bg-white px-8 py-4">
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={onBack}
            disabled={saveMutation.isPending}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-6 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <X className="h-4 w-4" />
            Cancel
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || layoutQuery.isLoading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {saveMutation.isPending ? "Retriggering…" : "Retrigger"}
          </button>
        </div>
      </footer>
    </div>
  );
}

// -------------------------------------------------------------- helpers

function KeyValueRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[180px_1fr] items-center gap-4 px-6 py-3">
      <div className="text-sm font-medium text-gray-700">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function CollapsibleSection({
  title,
  open,
  onToggle,
  children,
  right,
  variant = "default",
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  right?: React.ReactNode;
  variant?: "default" | "nested";
}) {
  const wrapperClass =
    variant === "nested"
      ? "overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
      : "overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm";
  return (
    <section className={wrapperClass}>
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        className="flex w-full cursor-pointer items-center gap-2 bg-gray-50 px-6 py-3 hover:bg-gray-100"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronRightIcon className="h-4 w-4 text-gray-500" />
        )}
        <span className="text-sm font-semibold text-gray-900">{title}</span>
        {right ? <div className="ml-auto">{right}</div> : null}
      </div>
      {open ? <div className="divide-y divide-gray-100">{children}</div> : null}
    </section>
  );
}

function MethodSelect({
  value,
  onChange,
  options,
  loading,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  loading?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
        value ? "text-gray-900" : "text-gray-400"
      }`}
    >
      <option value="" disabled>
        {loading ? "Loading…" : "Select an option"}
      </option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {formatMethodLabel(opt)}
        </option>
      ))}
    </select>
  );
}

function CoordInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0"
        className="w-20 rounded-md border border-gray-200 bg-white px-2 py-1 text-center text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
    </div>
  );
}

function KeywordList({
  keywords,
  onAdd,
  onChange,
  onRemove,
}: {
  keywords: string[];
  onAdd: () => void;
  onChange: (i: number, v: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {keywords.map((kw, i) => (
        <div
          key={i}
          className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 pl-2 pr-1 py-1"
        >
          <input
            type="text"
            value={kw}
            onChange={(e) => onChange(i, e.target.value)}
            placeholder="keyword"
            className="w-32 bg-transparent text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => onRemove(i)}
            className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-rose-500"
            title="Remove"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={onAdd}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-dashed border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
        title="Add keyword"
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
